import { Inject, Injectable, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { of, Subscription } from "rxjs";
import { catchError } from "rxjs/operators";
import { API_BASE_URL } from "src/swagger/api.client.generated";
import { UserService } from "@app/authentication/user.service";
import { DateHelper } from "../core/helpers/date.helper";

export type UserActivityEventType = "Foreground" | "Background";

/**
 * Tells the API when the web app is opened and closed (FLIP-326), so BetaMonitor's "Last Active"
 * reflects use rather than only logins - someone who stays logged in and reopens the tab each day
 * has no other signal.
 *
 * - Foreground: at boot, when the tab becomes visible, and straight after a login.
 * - Background: when the tab is hidden or the page goes away, sent with fetch keepalive (the same
 *   unload transport as TrackingDispatchService - an in-flight XHR is cancelled with the page, and
 *   sendBeacon cannot carry the Authorization header).
 *
 * Only transitions are sent: a Foreground is not repeated until a Background (or a login/logout)
 * has happened, and a Background is only sent after a Foreground, so the visibilitychange and
 * pagehide pair on a tab close is one row, not two.
 *
 * Nothing is sent without a signed-in user (the endpoint is JWT-protected and a 401 logs the user
 * out), and nothing is sent when this web app is running inside the mobile app's WebView: the
 * native app reports its own foreground/background (FLIP-557), and counting both would double
 * every open. Failures are swallowed; tracking must never get in the user's way.
 */
@Injectable({
  providedIn: "root",
})
export class UserActivityService implements OnDestroy {
  private lastSent: UserActivityEventType | null = null;
  private loginSubscription: Subscription | null = null;

  private readonly onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      this.sendForeground();
    } else {
      this.sendBackground();
    }
  };
  private readonly onPageHide = () => this.sendBackground();

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private apiBaseUrl: string,
    private userService: UserService
  ) {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("pagehide", this.onPageHide);

    // A different (or no) user is a fresh start: the new session's first open must be counted.
    this.loginSubscription = this.userService.userLoginStatusChanged$.subscribe(() => {
      this.lastSent = null;
      this.sendForeground();
    });

    this.sendForeground();
  }

  ngOnDestroy(): void {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("pagehide", this.onPageHide);
    this.loginSubscription?.unsubscribe();
  }

  private sendForeground(): void {
    if (document.visibilityState !== "visible" || this.lastSent === "Foreground" || !this.canReport()) {
      return;
    }

    this.lastSent = "Foreground";
    this.http
      .post(`${this.apiBaseUrl}/api/activity/events`, { eventType: "Foreground", platform: "Web" })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  private sendBackground(): void {
    if (this.lastSent !== "Foreground" || !this.canReport()) {
      return;
    }

    this.lastSent = "Background";

    // Raw fetch bypasses JwtInterceptor, so the token and the local-time headers are attached here.
    const headers: { [key: string]: string } = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.userService.getAuthToken()}`,
      "X-Local-Time": DateHelper.formatToApiDateTime(),
      "X-Tz-Offset": String(-new Date().getTimezoneOffset()),
    };

    try {
      fetch(`${this.apiBaseUrl}/api/activity/events`, {
        method: "POST",
        headers,
        body: JSON.stringify({ eventType: "Background", platform: "Web" }),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // Losing a close event is acceptable; throwing during teardown is not.
    }
  }

  /** A real session (not the pre-login pending token), and not the mobile app's WebView. */
  private canReport(): boolean {
    if (!this.userService.isUserLoggedIn() || !this.userService.getAuthToken()) {
      return false;
    }

    try {
      const appPlatform = localStorage.getItem("appPlatform");
      return appPlatform !== "Android" && appPlatform !== "iOS";
    } catch {
      return true;
    }
  }
}
